import {useEffect, useId, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {FaComment, FaEdit, FaEllipsisH, FaQuoteLeft, FaShare, FaSmile, FaTrash} from 'react-icons/fa';
import './MessageActions.css';

export const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];

/** Actions float over the row; menus live outside the virtual scroller. */
export default function MessageActions({onReply, onQuote, onForward, onEdit, onDelete, onReaction, onAddTask, onAddComplaint, disabled, children}) {
    const [menu, setMenu] = useState(null);
    const toolbar = useRef(null);
    const popover = useRef(null);
    const more = useRef(null);
    const id = useId();
    const close = (restoreFocus = false) => {
        if (restoreFocus) menu?.anchor?.focus();
        setMenu(null);
    };
    function open(kind, anchor) {
        const rect = anchor.getBoundingClientRect();
        const height = kind === 'emoji' ? 60 : 290 + (onAddTask ? 40 : 0) + (onAddComplaint ? 40 : 0);
        setMenu({kind, anchor, left: Math.max(8, Math.min(rect.right - 240, window.innerWidth - 248)), top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - height - 8))});
    }
    useEffect(() => {
        if (!menu) return undefined;
        popover.current?.querySelector('button:not(:disabled)')?.focus();
        const outside = event => {
            if (!popover.current?.contains(event.target) && !toolbar.current?.contains(event.target)) setMenu(null);
        };
        const resize = () => setMenu(null);
        document.addEventListener('pointerdown', outside);
        document.addEventListener('scroll', outside, true);
        window.addEventListener('resize', resize);
        return () => {
            document.removeEventListener('pointerdown', outside);
            document.removeEventListener('scroll', outside, true);
            window.removeEventListener('resize', resize);
        };
    }, [menu]);
    function select(action) { close(); action?.(); }
    function keyDown(event) {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
        if (event.key === 'Tab') { close(true); return; }
        if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = [...(popover.current?.querySelectorAll('button:not(:disabled)') || [])];
        const index = buttons.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : ['ArrowDown', 'ArrowRight'].includes(event.key) ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
        buttons[next]?.focus();
    }
    return <>
        <div ref={toolbar} className={`chat-message-toolbar${menu ? ' is-open' : ''}`} role="group" aria-label="Message actions">
            {onReaction ? <button type="button" className="chat-message-quick-action" aria-label="Add reaction" title="Add reaction" disabled={disabled} onClick={event => open('emoji', event.currentTarget)}><FaSmile/></button> : null}
            {onReply ? <button type="button" className="chat-message-quick-action" aria-label="Reply in thread" title="Reply in thread" onClick={onReply}><FaComment/></button> : null}
            {onForward ? <button type="button" className="chat-message-quick-action" aria-label="Forward message" title="Forward message" onClick={onForward}><FaShare/></button> : null}
            <button ref={more} type="button" aria-label="More message actions" title="More message actions" aria-haspopup="menu" aria-expanded={Boolean(menu)} aria-controls={menu ? id : undefined}
                onClick={event => menu ? close() : open('more', event.currentTarget)} onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open('more', event.currentTarget); } }}><FaEllipsisH/></button>
        </div>
        {menu ? createPortal(<div ref={popover} id={id} role="menu" aria-label={menu.kind === 'emoji' ? 'Choose reaction' : 'More message actions'} className={`chat-message-action-menu${menu.kind === 'emoji' ? ' is-emoji' : ''}`} style={{left: menu.left, top: menu.top}} onKeyDown={keyDown}
            onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !toolbar.current?.contains(event.relatedTarget)) close(); }}>
            {menu.kind === 'emoji' ? REACTION_EMOJIS.map(emoji => <button type="button" role="menuitem" aria-label={`React ${emoji}`} key={emoji} disabled={disabled} onClick={() => select(() => onReaction(emoji))}>{emoji}</button>) : <>
                {onReaction ? <button type="button" role="menuitem" disabled={disabled} onClick={() => open('emoji', more.current)}><FaSmile/>Add reaction</button> : null}
                {onReply ? <button type="button" role="menuitem" onClick={() => select(onReply)}><FaComment/>Reply in thread</button> : null}
                {onQuote ? <button type="button" role="menuitem" onClick={() => select(onQuote)}><FaQuoteLeft/>Quote message</button> : null}
                {onForward ? <button type="button" role="menuitem" onClick={() => select(onForward)}><FaShare/>Forward message</button> : null}
                {onEdit ? <button type="button" role="menuitem" disabled={disabled} onClick={() => select(onEdit)}><FaEdit/>Edit message</button> : null}
                {children}
                {onAddTask ? <button type="button" role="menuitem" onClick={() => select(onAddTask)}>Add Task</button> : null}
                {onAddComplaint ? <button type="button" role="menuitem" onClick={() => select(onAddComplaint)}>Add Complaint</button> : null}
                {onDelete ? <button type="button" role="menuitem" className="is-danger" disabled={disabled} onClick={() => select(onDelete)}><FaTrash/>Delete message</button> : null}
            </>}
        </div>, document.body) : null}
    </>;
}
